﻿import { Router } from '@angular/router';
import { HttpUnauthorized } from '../models/errors/http-Unauthorized';
import { HttpForbidden } from '../models/errors/http-Forbidden';
import { HttpError } from '../models/errors/HttpError';
import { InvalidInputError } from '../models/errors/InvalidInputError';
import { GlobalService } from './global.service';
import { Injectable } from '@angular/core';
import { Http, Headers } from '@angular/http';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';

import { ILoginRequest } from '../models/ss-api/requests/login.request';
import { ILoginResponse, LoginResponse } from '../models/ss-api/responses/login.response';
import { IBaseResponse, BaseResponse } from '../models/comm-http/responses/BaseResponse';
import { CommHttpService } from './comm-http.service';
import { ISendTwoFactorCodeOptionRequest } from '../models/ss-api/requests/send.two.factor.code.option.request';
import { ITwoFactorAuthenticationRequest } from '../models/ss-api/requests/two.factor.authentication.request';
import { KeyValuePair } from '../models/KeyValuePair';
import { UserRolesService } from './user.roles.service';
import { map, catchError } from 'rxjs/operators';

class AuthUrls {
    static readonly API_URL_BasicAuthentication: string = 'Authentication/Login';
    static readonly API_URL_SendTwoFactorCodeOption: string = 'Authentication/SendTwoFactorCodeOption';
    static readonly API_URL_TwoFactorAuthentication: string = 'Authentication/TwoFactorAuthentication';
}

@Injectable()
export class AuthService extends CommHttpService {

    public IsBusy: boolean = false;
    public onTwoFactorCodeRequired = new Subject<KeyValuePair<string, string>[]>();
    public onPartialAuthenticated = new Subject();
    public onFullyAuthenticated = new Subject<boolean>();
    public onTwoFactorCodeSent = new Subject();
    public OnAuthenticationResponse = new Subject<IBaseResponse<LoginResponse>>();
    public TwoFactorOptions: KeyValuePair<string, string>[];

    private IsFullyAuthenticated: boolean = false;
    private IsPartialAuthenticated: boolean = false;
    private Token: string;
    private TokenExpiration: Date;

    constructor(private http: Http, private gb: GlobalService, private router: Router, private UserRoles: UserRolesService ) {
        super(http);

        /*
        let authSub = this.onAuthError.subscribe(
            (statusText) =>{
                authSub.unsubscribe();
                this.router.navigate(['/authentication']);
            }
        )
        */
    }


    public SetIsPartialAuthenticated(): void {
        this.IsFullyAuthenticated = false;
        this.IsPartialAuthenticated = true;
        this.onPartialAuthenticated.next();
    }

    public SetIsFullyAuthenticated(): void {
        this.IsFullyAuthenticated = true;
        this.IsPartialAuthenticated = true;
        this.onFullyAuthenticated.next();
    }


    public CheckIsFullyAuthenticated(): boolean {
        return this.IsFullyAuthenticated;
    }

    public CheckIsPartialAuthenticated(): boolean {
        return this.IsPartialAuthenticated;
    }


    public AuthenticationBasic(loginRequest: ILoginRequest): Observable<any> {

        this.SetBusy();
        // checks
        if (!loginRequest.CheckEmpty()) {
            return Observable.throw(new InvalidInputError('Username or Password is empty!'));
        }

        // prepare headers
        let headers: Headers = new Headers();
        headers.append('Authorization', 'Basic ' + btoa(loginRequest.Username + ':' + loginRequest.Password));

        // request
        return this.Post<ILoginResponse>(AuthUrls.API_URL_BasicAuthentication, loginRequest, headers).pipe(
            map(response => {

                if (response.Data.Authenticated == true) {
                    // we are authenticated
                    // we can save the token in sessionstorage if we don't want to use cookies.
                    // we are going to use cookies
                    this.ProcessAuthenticationLoginResponse(response.Data);

                } else {
                    return Observable.throw(new HttpForbidden(response, 'Authentication Error', response.Message));
                }

                this.OnAuthenticationResponse.next(response);
                this.UnsetBusy();
                return response;

            }),
            catchError((error) => {

                if (error instanceof HttpUnauthorized) {
                    error.FriendlyTitle = 'Authorization Error';
                    error.FriendlyMessage = 'Wrong username or password';
                    if (this.IsFullyAuthenticated) {
                        // session expired
                        console.log("lr2");
                        this.router.navigate(['/authentication']);
                        return Observable.throw(error);
                    }
                }

                this.UnsetBusy();

                return Observable.throw(error);
            })
        )

    }

    public ProcessAuthenticationLoginResponse(response: LoginResponse) {

        this.Token = response.Token;
        this.TokenExpiration = response.TokenExpiration;
        response.Redirect = response.Redirect;

        this.gb.AdminId = response.AdminId;
        this.gb.FirstName = response.FirstName;
        this.gb.LastName = response.LastName;
        this.gb.Username = response.Username;
        this.gb.Email = response.Email;

        if (response.TwoFactorRequired) {
            // we need a twofactor authentication
            this.TwoFactorOptions = response.TwoFactorOptions;
            this.onTwoFactorCodeRequired.next(this.TwoFactorOptions);
            this.SetIsPartialAuthenticated();
        } else {
            this.SetIsFullyAuthenticated();
        }

    }

    public AuthenticateTwoFactor(twoFactorAuthenticationRequest: ITwoFactorAuthenticationRequest): Observable<IBaseResponse<any>> {

        this.SetBusy();
        let response: IBaseResponse<any>;

        // inject token
        twoFactorAuthenticationRequest.Token = this.Token;

        // checks
        if (twoFactorAuthenticationRequest.Code === '') {
            response = new BaseResponse<any>();
            response.Result = false;
            response.Message = 'Code is empty!'

            this.UnsetBusy();
            return Observable.create(observer => {
                observer.next(response);
                observer.complete();
            });
        }

        // request
        return this.Post<any>(AuthUrls.API_URL_TwoFactorAuthentication, twoFactorAuthenticationRequest).pipe(
            map(response => {

                if (response.Result) {
                    this.SetIsFullyAuthenticated();
                }

                this.UnsetBusy();
                return response;

            }),
            catchError((error: HttpError) => {

                if (error instanceof HttpUnauthorized) {
                    error.FriendlyTitle = 'Authorization Error';
                    error.FriendlyMessage = 'Wrong code';
                }

                this.UnsetBusy();
                return Observable.throw(error);
            })

        )



    }

    public RequestTwoFactorCode(twoFactorOption: ISendTwoFactorCodeOptionRequest): Observable<IBaseResponse<any>> {
        this.SetBusy();

        twoFactorOption.Token = this.Token;
        let response: IBaseResponse<any>;

        // request
        return this.Post<any>(AuthUrls.API_URL_SendTwoFactorCodeOption, twoFactorOption).pipe(
            map(response => {

                if (response.Result == true) {
                    // code sent
                    this.onTwoFactorCodeSent.next();
                }

                this.UnsetBusy();
                return response;

            }),
            catchError((error: HttpError) => {

                this.UnsetBusy();
                return Observable.throw(error);
            })

        )

    }

}
